// niceScale picks the gridlines for a bar chart: a top-of-axis value at or
// above the tallest bar, and evenly spaced rules below it.
//
// Every chart in this app is a count of something per day or per month, so
// the step is always a whole number, and only ever 1, 2 or 5 times a power
// of ten — the conventional "nice number" set, which is what makes a value
// readable off the axis without arithmetic. A 4-high series therefore rules
// at 1, 2, 3, 4 and a 35-high one at 10, 20, 30, 40, rather than either
// being scaled to the same anonymous full-height bar the charts drew before.
//
// Bars are drawn against the returned max, not against the raw data max, so
// the tallest bar stops at the top gridline instead of at the top of the
// track. Series that share an axis (commits, merges and issues closed on
// one day chart) must all be scaled by one call's result, or the comparison
// between them lies.
const targetIntervals = 5;

export function niceScale(dataMax: number): { max: number; ticks: number[] } {
  // An all-zero series still needs an axis to draw against; 1 keeps every
  // bar at zero height rather than dividing by zero.
  if (!Number.isFinite(dataMax) || dataMax <= 0) return { max: 1, ticks: [1] };

  const step = niceStep(dataMax / targetIntervals);
  const max = Math.ceil(dataMax / step) * step;
  const ticks: number[] = [];
  for (let t = step; t <= max; t += step) ticks.push(t);
  return { max, ticks };
}

// niceStep rounds a raw interval up to the nearest 1, 2 or 5 times a power
// of ten, with a floor of 1 — a gridline at 0.5 of a commit means nothing.
function niceStep(raw: number): number {
  if (raw <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const mantissa = raw / magnitude;
  const nice = mantissa <= 1 ? 1 : mantissa <= 2 ? 2 : mantissa <= 5 ? 5 : 10;
  return nice * magnitude;
}
